import React from 'react';
import { Line } from 'react-chartjs-2';

import zoomPlugin from 'chartjs-plugin-zoom';
import 'chartjs-adapter-date-fns'; // needed for the TimeScale

import {
  Chart as ChartJS,
  CategoryScale,
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
  ChartData,
} from 'chart.js';

import { drawCurrentDateLine } from '@UI-Elements/ChartPlugins/CurrentDateLinePlugin';
import { drawLinePlugin } from '@UI-Elements/ChartPlugins/DrawLinePlugin';
import { handleMouseDown, handleMouseMove, handleMouseUp } from '@UI-Elements/ChartPlugins/ChartUtils';
import { IndexRoutes } from './HistoricalUtils';

ChartJS.register(
  CategoryScale,
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  zoomPlugin,
);

// Align chart typography with global app font (Space Grotesk / Inter stack)
ChartJS.defaults.font = {
  ...ChartJS.defaults.font,
  family: "'Space Grotesk', 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};


const getOptions = (
  onDataClick: (_dataIndex: number, _x: number) => void,
  minIndex: number,
  xPoint: number,
  currency: string,
  isDrawPrice: boolean | undefined,
): ChartOptions<'line'> => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  plugins: {
    legend: false,
    title: { display: false },
    tooltip: {
      mode: 'index',
      intersect: false,
      backgroundColor: '#1f2937',
      titleColor: '#f3f4f6',
      bodyColor: '#fff',
      titleFont: { size: 11, weight: 'normal' },
      bodyFont: { size: 12, weight: 'bold' },
      padding: { top: 8, bottom: 8, left: 12, right: 12 },
      cornerRadius: 8,
      displayColors: true,
      boxWidth: 8,
      boxHeight: 8,
      boxPadding: 4,
    },
    drawCurrentDateLine: true,
    drawLinePlugin: { minIndex, xPoint },
    zoom: {
      zoom: {
        wheel: { enabled: true },
        pinch: { enabled: true },
        mode: 'xy',
      },
      pan: {
        enabled: false,
        mode: 'xy',
      },
    },
  } as any,
  onHover: (event: any) => {
    if (event.type === 'click') {
      const activeElements = event.chart.tooltip?.getActiveElements();
      if (activeElements?.length) {
        const {
          index: dataIndex,
          element: { x },
        } = activeElements[0];
        const topY = event.chart.scales.y.top;
        const bottomY = event.chart.scales.y.bottom;
        const clickedData = { dataIndex, topY, bottomY, x };
        event.chart.clicked = clickedData;
        onDataClick(clickedData.dataIndex, x);
      }
    }
  },
  scales: {
    x: {
      type: 'time' as const,
      time: {
        unit: 'month' as const,
        displayFormats: { month: 'MM/yy' },
      },
      title: { display: false },
      ticks: {
        display: true,
        autoSkip: true,
        font: { size: 11 },
        color: '#9ca3af',
      },
      grid: { display: false },
      border: { display: false },
    },
    y: {
      beginAtZero: false,
      offset: true,
      title: {
        display: true,
        text: isDrawPrice ? `Material Price (${currency})` : 'Forward Premium %',
        font: { size: 11, weight: 'normal' },
        color: '#6b7280',
      },
      ticks: {
        font: { size: 11 },
        color: '#9ca3af',
      },
      grid: {
        color: (context) => (context.tick.value === 0 ? 'rgba(107,114,128,0.4)' : 'rgba(0,0,0,0.04)'),
        lineWidth: (context) => (context.tick.value === 0 ? 1.5 : 1),
      },
      border: { display: false },
    },
  },
  elements: { line: { tension: 0.25 }, point: { radius: 0, hoverRadius: 4 } },
});

const getData = (materialRoutes: any[], materialColor: string, drawPrice?: boolean): ChartData<'line'> => ({
  labels: materialRoutes.map((r) => r.date),
  datasets: drawPrice
    ? [
        {
          label: `Spot Price`,
          data: materialRoutes.map((d) => ({
            x: new Date(d.date).getTime(),
            y: d.price,
          })),
          borderColor: materialColor,
          backgroundColor: hexToRgba(materialColor, 0.06),
          fill: true,
          borderWidth: 2,
        },
        {
          label: 'Future Price',
          data: materialRoutes.map((d) => ({
            x: new Date(d.date).getTime(),
            y: d.future,
          })),
          borderColor: '#10b981',
          backgroundColor: 'rgba(16, 185, 129, 0.04)',
          fill: true,
          borderWidth: 2,
          borderDash: [2, 2],
        },
      ]
    : [
        {
          label: 'Forward Premium',
          data: materialRoutes.map(({ date, price, future }) => ({
            x: new Date(date).getTime(),
            y: ((future - price) / price) * 100,
          })),
          borderColor: '#10b981',
          backgroundColor: 'rgba(16, 185, 129, 0.06)',
          fill: true,
          borderWidth: 1.5,
        },
      ],
});

interface Props {
  indexRoutesData: IndexRoutes[];
  onDataClick: (_priceData: IndexRoutes, _x: number) => void;
  materialColor: string;
  currency: string;
  lineXcoord: number;
  isDrawPrice?: boolean;
  compact?: boolean;
}
export const HistoricalChart: React.FC<Props> = ({
  indexRoutesData,
  onDataClick,
  materialColor,
  currency,
  lineXcoord,
  isDrawPrice,
  compact,
}) => {
  const chartRef = React.useRef();

  React.useEffect(() => {
    //@ts-expect-error-type-not-match
    const chart = chartRef.current as ChartJS<'line'>;
    if (chart?.canvas) {
      chart.canvas.addEventListener('mousedown', handleMouseDown(chart));
      chart.canvas.addEventListener('mousemove', handleMouseMove(chart));
      chart.canvas.addEventListener('mouseup', handleMouseUp(chart));
    }

    return () => {
      if (chart?.canvas) {
        chart.canvas.removeEventListener('mousemove', handleMouseMove(chart));
        chart.canvas.removeEventListener('mousedown', handleMouseDown(chart));
        chart.canvas.removeEventListener('mouseup', handleMouseUp(chart));
      }
    };
  }, []);

  const innerClick = (dIndex: number, x: number) => {
    const point = indexRoutesData[dIndex];
    if (!point) return;
    onDataClick(point, x);
  };

  return (
    <div className={compact ? 'h-[170px]' : 'flex-1 min-h-[300px]'}>
      {indexRoutesData && indexRoutesData.length > 0 ? (
        <Line
          options={getOptions(innerClick, indexRoutesData.length - 12, lineXcoord, currency, isDrawPrice)}
          data={getData(indexRoutesData, materialColor || '#6b7280', isDrawPrice)}
          plugins={[drawLinePlugin, drawCurrentDateLine]}
          ref={chartRef}
        />
      ) : (
        <div className="flex items-center justify-center h-full text-xs text-gray-400">
          No data for this account / material yet
        </div>
      )}
    </div>
  );
};

// Lightweight utility to convert a hex color (#RGB or #RRGGBB) into an rgba() string
function hexToRgba(hex: string, alpha: number): string {
  let cleaned = hex.replace('#', '');

  if (cleaned.length === 3) {
    cleaned = cleaned
      .split('')
      .map((c) => c + c)
      .join('');
  }

  const r = parseInt(cleaned.substring(0, 2), 16) || 0;
  const g = parseInt(cleaned.substring(2, 4), 16) || 0;
  const b = parseInt(cleaned.substring(4, 6), 16) || 0;

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
